// help-dock.component.ts
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewChecked,
  Injector,
  Renderer2
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize, Subscription } from 'rxjs';
import { HelpCenterDocBean } from 'src/app/features/setting/pages/help-center-setting/model/HelpCenterDocBean';
import { HelpCenterResp } from 'src/app/features/setting/pages/help-center-setting/model/HelpCenterResp';
import { HelpCenterService } from 'src/app/features/setting/service/help-center.service';
import { HelpCenterImageService } from '../../service/help-center-image.service';
import { HelpCenterDialogComponent } from '../help-center-dialog/help-center-dialog.component';
import { SessionBaseComponent } from '../session.base.component';
import { HelpCenterPanelService } from '../../service/help-center-panel.service';


type Prefetch = {
  guideHeader?: string;
  releasedGuideContent?: string;
  releasedGuideContentIT?: string;
};

@Component({
  selector: 'help-dock',
  templateUrl: './help-dock.component.html',
  styleUrls: ['./help-dock.component.scss'],
  providers: [DialogService]
})
export class HelpDockComponent extends SessionBaseComponent implements OnInit, OnDestroy, AfterViewChecked {

  @Input() permissionCode!: string;
  @Input() lang?: string;

  @Input() miniWidth: string = '35vw';
  @Input() miniMinWidth: string = '320px';
  @Input() miniMaxWidth: string = '500px';
  @Input() showMiniToc: boolean = false;

  @Input() prefetch?: Prefetch;

  loadingCnt = 0;
  open = false;
  pinned = false;
  activeTabIndex: number = 0; // 0=使用者說明, 1=IT說明, 2=附件下載

  headerTitle = '';
  releasedUserHtml: SafeHtml = '';
  releasedItHtml: SafeHtml = '';

  docs: HelpCenterDocBean[] = [];

  @ViewChild('userBody', { static: false }) userBodyRef!: ElementRef<HTMLElement>;
  @ViewChild('itBody',   { static: false }) itBodyRef!: ElementRef<HTMLElement>;
  @ViewChild('dockElement', { static: true }) dockElement!: ElementRef<HTMLElement>;

  tocUser: { id: string; text: string; level: number }[] = [];
  tocIT:   { id: string; text: string; level: number }[] = [];
  activeId?: string;

  private sub?: Subscription;
  private imageHandlersSetup = false;
  private movedToBody = false;

  constructor(
    injector: Injector,
    private api: HelpCenterService,
    private sanitizer: DomSanitizer,
    private dialog: DialogService,
    private panelBus: HelpCenterPanelService,
    private router: Router,
    private http: HttpClient,
    private elementRef: ElementRef,
    private imageService: HelpCenterImageService
  ) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    const isNarrow = window.matchMedia('(max-width: 1024px)').matches;
    if (isNarrow) {
      return;
    }

    // 將 Dock 移到 body 最外層，避免父層 transform 影響
    this.moveToBody();

    if (this.prefetch?.guideHeader ||
        this.prefetch?.releasedGuideContent ||
        this.prefetch?.releasedGuideContentIT) {
      this.applyPrefetch(this.prefetch);
    } else {
      this.fetchGuide();
    }

    // 設定 hover 事件（延遲執行，確保元素已移到 body）
    setTimeout(() => {
      this.setupHoverEvents();
    }, 100);
  }

  ngAfterViewChecked(): void {
    if (!this.imageHandlersSetup && this.open) {
      this.setupImageClickHandlers();
      this.convertImageUrlsToBlobs();
      this.imageHandlersSetup = true;
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe?.();

    // 從 body 移除
    this.removeFromBody();
  }

  /**
   * 將 Dock 移到 body，避免父層 transform 影響 position: fixed
   */
  private moveToBody(): void {
    if (this.movedToBody) {
      return;
    }

    const element = this.elementRef.nativeElement;
    this.renderer.appendChild(document.body, element);
    this.movedToBody = true;
  }

  /**
   * 從 body 移除
   */
  private removeFromBody(): void {
    if (!this.movedToBody) {
      return;
    }

    const element = this.elementRef.nativeElement;
    if (element.parentNode === document.body) {
      this.renderer.removeChild(document.body, element);
    }
    this.movedToBody = false;
  }

  /**
   * Tab 切換事件
   */
  onTabChange(index: number): void {
    this.activeTabIndex = index;
    // 清除圖片處理器標記，讓新 Tab 的圖片重新綁定事件
    this.imageHandlersSetup = false;
  }

  /**
   * 設定 hover 事件（使用原生 DOM）
   */
  private setupHoverEvents(): void {
    const element = this.elementRef.nativeElement;
    const dockEl = element.querySelector('.help-dock');
    const tabEl = dockEl?.querySelector('.tab');

    if (tabEl) {
      // Tab hover 時展開
      tabEl.addEventListener('mouseenter', () => {
        if (!this.pinned) {
          this.open = true;
        }
      });

      // 離開 dock 整個區域時收回
      dockEl?.addEventListener('mouseleave', () => {
        if (!this.pinned) {
          this.open = false;
        }
      });
    }
  }

  togglePin(): void {
    this.pinned = !this.pinned;
    if (this.pinned) {
      this.open = true;
    }
  }

  openFullPanel(): void {
    this.panelBus.open({
      permissionCode: this.permissionCode,
      lang: this.lang,
      guideHeader: this.headerTitle,
      releasedGuideContent: this.unwrap(this.releasedUserHtml),
      releasedGuideContentIT: this.unwrap(this.releasedItHtml)
    });
  }

  openDialog(): void {
    this.dialog.open(HelpCenterDialogComponent, {
      data: {
        permissionCode: this.permissionCode,
        lang: this.lang
      },
      header: this.headerTitle || 'Help',
      width: '68vw',
      height: '80vh',
      dismissableMask: true
    });
  }

  navigateToSetting(): void {
    this.routeStateService.navigateTo('/Setting/HelpCenter', {
      queryParams: {
        permissionCode: this.permissionCode,
        lang: this.lang || 'zh-TW'
      }
    });
  }

  /**
   * 將 HTML 中的圖片 URL 轉換成 blob URL
   */
  private convertImageUrlsToBlobs(): void {
    requestAnimationFrame(() => {
      const element = this.elementRef.nativeElement;
      const images = element.querySelectorAll('.body img');

      images.forEach((img: Element) => {
        const imgElement = img as HTMLImageElement;
        const src = imgElement.getAttribute('src');
        const originalSrc = imgElement.getAttribute('data-original-src');

        if (imgElement.getAttribute('data-blob-loading') === 'true') {
          imgElement.removeAttribute('data-blob-loading');
        }

        if (!src || src.startsWith('blob:')) {
          return;
        }

        const targetUrl = originalSrc || src;
        if (this.isProtectedImageUrl(targetUrl)) {
          this.loadImageAsBlob(targetUrl, imgElement);
        }
      });
    });
  }

  private loadImageAsBlob(url: string, imgElement: HTMLImageElement): void {
    imgElement.setAttribute('data-original-src', url);
    imgElement.setAttribute('data-blob-loading', 'true');
    const requestUrl = this.resolveProtectedImageUrl(url);
    this.http.get(requestUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        imgElement.src = blobUrl;
        imgElement.removeAttribute('data-blob-loading');

        this.ensureImageStyle(imgElement);
      },
      error: (err) => {
        imgElement.removeAttribute('data-blob-loading');
        console.error('Failed to load image:', url, err);
      }
    });
  }
  private ensureImageStyle(img: HTMLImageElement): void {
    const currentStyle = img.getAttribute('style') || '';
    if (!currentStyle.includes('max-width')) {
      img.setAttribute('style',
        'max-width: 100%; height: auto; display: block; margin: 1rem 0; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);'
      );
    }
  }

  private setupImageClickHandlers(): void {
    requestAnimationFrame(() => {
      const element = this.elementRef.nativeElement;
      const images = element.querySelectorAll('.help-dock .body img');
      images.forEach((img: { cloneNode: (arg0: boolean) => any; parentNode: { replaceChild: (arg0: any, arg1: any) => void; }; }) => {
        const clonedImg = img.cloneNode(true);
        img.parentNode?.replaceChild(clonedImg, img);

        clonedImg.addEventListener('click', (e: { target: HTMLImageElement; }) => {
          const target = e.target as HTMLImageElement;
          this.openImagePreview(target.src);
        });
      });
    });
  }

  private openImagePreview(src: string): void {
    window.open(src, '_blank', 'noopener,noreferrer');
  }

  /** 保留 Tab 和連續空白，讓預覽正確顯示手動縮排 */
  private preserveWhitespace(html: string): string {
    return html
      .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
      .replace(/ {2}/g, '&nbsp;&nbsp;');
  }

  private prepareProtectedImageHtml(html: string): string {
    if (!html || !this.containsProtectedImage(html)) {
      return html;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const images = wrapper.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('src') || '';
      if (!src || !this.isProtectedImageUrl(src)) {
        return;
      }

      if (img.getAttribute('data-blob-loading') === 'true') {
        img.removeAttribute('data-blob-loading');
      }

      img.setAttribute('data-original-src', src);
      img.setAttribute('src', 'about:blank');
    });

    return wrapper.innerHTML;
  }

  private isProtectedImageUrl(url: string): boolean {
    return url.includes('/HelpCenterImage/images/') || url.includes('/guide-center/v1/images/');
  }

  private containsProtectedImage(html: string): boolean {
    return html.includes('/HelpCenterImage/images/') || html.includes('/guide-center/v1/images/');
  }

  private resolveProtectedImageUrl(url: string): string {
    return this.imageService.resolveImageRequestUrl(url);
  }
  private applyPrefetch(p: Prefetch): void {
    const notSet = '<p>尚未設定內容</p>';
    this.headerTitle = p.guideHeader || this.permissionCode;
    const userHtml = this.prepareProtectedImageHtml(this.preserveWhitespace(p.releasedGuideContent || notSet));
    const itHtml = this.prepareProtectedImageHtml(this.preserveWhitespace(p.releasedGuideContentIT || notSet));
    this.releasedUserHtml =
      this.sanitizer.bypassSecurityTrustHtml(userHtml);
    this.releasedItHtml =
      this.sanitizer.bypassSecurityTrustHtml(itHtml);
    this.fetchDocs();

    if (this.showMiniToc) {
      this.refreshTocLater('user');
      this.refreshTocLater('it');
    }

    this.imageHandlersSetup = false;
  }

  private fetchGuide(): void {
    if (!this.permissionCode) {
      return;
    }

    this.loadingCnt++;
    this.api.getHelpCenterByPermissionCode(this.permissionCode)
      .pipe(finalize(() => this.loadingCnt--))
      .subscribe({
        next: (resp: HelpCenterResp) => {
          const notSet = '<p>尚未設定內容</p>';
          this.headerTitle = resp.guideHeader || this.permissionCode;
          const userHtml = this.prepareProtectedImageHtml(this.preserveWhitespace(resp.releasedGuideContent || notSet));
          const itHtml = this.prepareProtectedImageHtml(this.preserveWhitespace(resp.releasedGuideContentIT || notSet));
          this.releasedUserHtml =
            this.sanitizer.bypassSecurityTrustHtml(userHtml);
          this.releasedItHtml =
            this.sanitizer.bypassSecurityTrustHtml(itHtml);

          this.fetchDocs();

          if (this.showMiniToc) {
            this.refreshTocLater('user');
            this.refreshTocLater('it');
          }

          this.imageHandlersSetup = false;
        }
      });
  }

  private fetchDocs(): void {
    if (!this.permissionCode) {
      return;
    }

    this.loadingCnt++;
    // 只查詢 USER 類別的文件
    this.api.getGuideDocList(this.permissionCode, this.laasSetting.language, 'UserFile')
      .pipe(finalize(() => this.loadingCnt--))
      .subscribe({
        next: (docs) => {
          this.docs = docs || [];
        }
      });
  }

  downloadDoc(row: HelpCenterDocBean): void {
    this.loadingCnt++;
    this.api.downloadDoc(row.sn)
      .pipe(finalize(() => this.loadingCnt--))
      .subscribe({
        next: (file) => {
          const url = URL.createObjectURL(file);
          const a = document.createElement('a');
          a.href = url;
          a.download = row.name ?? '';
          a.click();
          URL.revokeObjectURL(url);
        }
      });
  }

  /**
   * 格式化檔案大小
   */
  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  private buildToc(container: HTMLElement, prefix: 'user' | 'it') {
    if (!container) {
      return [];
    }
    const nodes = Array.from(container.querySelectorAll('h1, h2, h3')) as HTMLElement[];
    return nodes.map((el, i) => {
      if (!el.id) {
        el.id = `${prefix}-h-${i}`;
      }
      return {
        id: el.id,
        text: (el.innerText || '').trim(),
        level: Number(el.tagName.substring(1))
      };
    });
  }

  private refreshTocLater(kind: 'user' | 'it'): void {
    requestAnimationFrame(() => {
      const host =
        kind === 'user'
          ? this.userBodyRef?.nativeElement
          : this.itBodyRef?.nativeElement;
      const toc = this.buildToc(host, kind);
      if (kind === 'user') {
        this.tocUser = toc;
      } else {
        this.tocIT = toc;
      }
    });
  }

  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.activeId = id;
    }
  }

  private unwrap(safe: SafeHtml): string {
    return (safe as any)?.changingThisBreaksApplicationSecurity ?? '';
  }
}
